import React, {
  useCallback,
} from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {
  makeStyles,
} from '@material-ui/styles';
import classNames from 'classnames';

import Template from 'Data/Template';
import {
  getSelectedFolder,
  getIsTemplateDialogLoading,
  getTemplateDialogFolders,
} from 'Src/store/selectors';
import {
  updateTemplate,
  fetchTemplateDialogData,
} from 'Src/actions';

import TemplateForm from './TemplateForm';
import Loader from './Loader';
import {
  HEIGHT,
  WIDTH,
} from './CreateTemplateDialog';

const useStyles = makeStyles({
  dialog: {
    minWidth: HEIGHT,
    minHeight: WIDTH,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

function EditTemplateDialog(props) {
  const {
    template,
    open,
    onClose,
  } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const selectedFolder = useSelector(getSelectedFolder);
  const isLoading = useSelector(getIsTemplateDialogLoading);
  const folders = useSelector(getTemplateDialogFolders);

  const handleDialogOpen = useCallback(() => dispatch(fetchTemplateDialogData()), [dispatch]);
  const handleClose = useCallback(() => onClose(), [onClose]);
  const handleSubmit = useCallback(({
    folder: folderId,
    name,
    subject,
    body,
  }) => {
    dispatch(
      updateTemplate({
        id: template.id,
        folderId,
        name,
        subject,
        body,
      }),
    );
    onClose();
  }, [dispatch, onClose, template.id]);

  return (
    <Dialog
      // The `disableEnforceFocus` property prevents the MUI dialog from stealing focus from the
      // form inputs within it.
      // See:
      // https://material-ui.com/api/modal/
      // https://github.com/GrooveLabs/febes/pull/4061
      disableEnforceFocus

      open={open}
      fullWidth
      maxWidth="md"
      onClose={onClose}
      onEnter={handleDialogOpen}
      classes={{
        paper: classes.dialog,
      }}
    >
      <DialogTitle>
        Edit Template
      </DialogTitle>
      <DialogContent className={classNames(
        {
          [classes.loading]: isLoading,
        },
      )}
      >
        { isLoading && <Loader /> }
        {
          /*
            TODO @jaebradley replace with correct initial folder value:
            1. Either your personal folder
            2. The most recently created folder
          */
         !isLoading
            && !folders.isEmpty()
            && (
              <TemplateForm
                initialFolderId={selectedFolder.id}
                folders={folders}
                initialBody={template.body}
                initialName={template.name}
                initialSubject={template.subject}
                onClose={handleClose}
                onSubmit={handleSubmit}
              />
            )
        }
      </DialogContent>
    </Dialog>
  );
}

EditTemplateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  template: ImmutablePropTypes.recordOf(Template),
};

EditTemplateDialog.defaultProps = {
  onClose: () => {},
  template: null,
};

export default EditTemplateDialog;
